import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@base-ui/react/select';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import chevronDownSvg from '@assets/analytics/chevron-down.svg?raw';
import fieldStyles from '@components/ui/Field/Field.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './PhoneField.module.css';

const E164_MAX_DIGITS = 15; // E.164 allows at most 15 digits, country calling code included

function flagEmoji(iso: string): string {
  return iso.replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function examplePlaceholder(country: CountryCode): string {
  const example = getExampleNumber(country, examples);
  return example ? example.formatNational().replace(/\d/g, '0') : '';
}

function capDigits(digits: string, country: CountryCode): string {
  const e164Room = E164_MAX_DIGITS - getCountryCallingCode(country).length;
  let capped = digits.slice(0, e164Room);
  while (capped.length > 0 && validatePhoneNumberLength(capped, country) === 'TOO_LONG') {
    capped = capped.slice(0, -1);
  }
  return capped;
}

interface IPhoneFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  defaultCountry?: CountryCode;
  invalidMessage?: string;
  onErrorChange?: (hasError: boolean) => void; // Fires when the field enters or leaves an error state 
}

export function PhoneField({
  label,
  value,
  onChange,
  defaultCountry = 'JP',
  invalidMessage,
  onErrorChange,
}: IPhoneFieldProps) {
  const { t, i18n } = useTranslation();

  // `i18n.language` is a language tag ('en' / 'ja') — not a country code; 'jp'
  // would be silently ignored by Intl. It is also undefined before i18n
  // initialises, and an unusable tag makes the DisplayNames constructor throw,
  // so resolve to a usable locale rather than letting the whole field crash.
  const locale = useMemo(() => {
    try {
      return Intl.DisplayNames.supportedLocalesOf([i18n.language])[0] ?? 'en';
    } catch {
      return 'en';
    }
  }, [i18n.language]);

  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return getCountries()
      .map((iso) => ({
        iso,
        dial: `+${getCountryCallingCode(iso)}`,
        name: (() => {
          try {
            return names.of(iso) ?? iso;
          } catch {
            return iso;
          }
        })(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);

  const parsedValue = useMemo(() => (value ? parsePhoneNumberFromString(value) : null), [value]);

  const [country, setCountry] = useState<CountryCode>(parsedValue?.country ?? defaultCountry);
  const [national, setNational] = useState(() =>
    parsedValue ? new AsYouType(parsedValue.country).input(parsedValue.nationalNumber) : '',
  );
  const lastReported = useRef(value);

  // Adopt values set by the caller rather than typed here (e.g. the dialog
  // being reopened for a different user).
  useEffect(() => {
    if (value === lastReported.current) return;
    lastReported.current = value;
    const parsed = value ? parsePhoneNumberFromString(value) : null;
    setCountry(parsed?.country ?? defaultCountry);
    setNational(parsed ? new AsYouType(parsed.country).input(parsed.nationalNumber) : '');
  }, [value, defaultCountry]);

  const parsedInput = useMemo(() => {
    if (!national.trim()) return null;
    return parsePhoneNumberFromString(national, country) ?? null;
  }, [national, country]);

  const hasDigits = /\d/.test(national);
  const isValid = parsedInput?.isValid() ?? false;
  // Digits typed that don't form a valid number. An empty field is not an
  // error — phone is optional wherever this is used today.
  const hasError = hasDigits && !isValid;

  // Held in a ref so an inline callback prop doesn't re-fire the effect on
  // every render; only an actual change of error state notifies the caller.
  // The ref is written in an effect, not during render — a render-phase write
  // is a side effect and misbehaves under concurrent rendering.
  const onErrorChangeRef = useRef(onErrorChange);
  useEffect(() => {
    onErrorChangeRef.current = onErrorChange;
  });
  useEffect(() => {
    onErrorChangeRef.current?.(hasError);
  }, [hasError]);

  const placeholder = useMemo(() => examplePlaceholder(country), [country]);

  /** Reports E.164 only for a valid number; anything else is "no number". */
  function report(nextNational: string, nextCountry: CountryCode) {
    const parsed = nextNational.trim()
      ? parsePhoneNumberFromString(nextNational, nextCountry)
      : null;
    const next = parsed?.isValid() ? parsed.number : '';
    lastReported.current = next;
    onChange(next);
  }

  function handleNationalChange(raw: string) {
    let digits = raw.replace(/\D/g, '');

    // Deleting a separator (the "-" in 090-1234-5678) removes no digit, and
    // AsYouType would immediately put it back — leaving backspace looking
    // stuck. Drop the preceding digit instead so deletion always progresses.
    if (raw.length < national.length && digits === national.replace(/\D/g, '')) {
      digits = digits.slice(0, -1);
    }

    digits = capDigits(digits, country);
    const nextNational = digits ? new AsYouType(country).input(digits) : '';
    setNational(nextNational);
    report(nextNational, country);
  }

  function handleCountryChange(nextIso: string | null) {
    if (!nextIso) return;
    const iso = nextIso as CountryCode;
    setCountry(iso);
    // Re-format the existing digits under the new country's rules.
    const digits = national.replace(/\D/g, '');
    const reformatted = digits ? new AsYouType(iso).input(digits) : '';
    setNational(reformatted);
    report(reformatted, iso);
  }

  const selected = countries.find((entry) => entry.iso === country);
  const showError = Boolean(invalidMessage) && hasError;
  if (!selected) return null;

  return (
    <div className={fieldStyles.field}>
      <span>{label}</span>

      <span className={`${fieldStyles.input} ${styles.control}`}>
        <Select.Root value={country} onValueChange={handleCountryChange}>
          <Select.Trigger
            aria-label={`${label} — ${t('common.country')}`}
            className={styles.trigger}
          >
            <span className={styles.flag} aria-hidden="true">
              {flagEmoji(selected.iso)}
            </span>
            <span className={styles.dial}>{selected.dial}</span>
            <Select.Icon className={styles.chevron}>
              <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: chevronDownSvg }} />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner
              sideOffset={6}
              alignItemWithTrigger={false}
              className={styles.positioner}
            >
              <Select.Popup className={styles.popup}>
                <ScrollArea>
                  {countries.map((entry) => (
                    <Select.Item key={entry.iso} value={entry.iso} className={styles.item}>
                      <span aria-hidden="true">{flagEmoji(entry.iso)}</span>
                      <Select.ItemText>{entry.name}</Select.ItemText>
                      <span className={styles.itemDial}>{entry.dial}</span>
                    </Select.Item>
                  ))}
                </ScrollArea>
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>

        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className={styles.number}
          value={national}
          onChange={(event) => handleNationalChange(event.target.value)}
          placeholder={placeholder}
          aria-label={label}
          aria-invalid={showError || undefined}
        />
      </span>

      {showError && <small className={styles.error}>{invalidMessage}</small>}
    </div>
  );
}
